// Bugfix 18 (#1353): Rollen & Rechten. Links de medewerkerslijst (zoeken en
// filteren gebeurt hier, in de browser), rechts het Turbo Frame met de gekozen
// medewerker. Deze controller houdt bij of er niet-opgeslagen wijzigingen zijn
// en vraagt dan eerst wat er moet gebeuren voordat je naar een collega gaat.
import { Controller } from "stimulus"

export default class extends Controller {
  static targets = [
    "zoekveld", "filterknop", "medewerker", "leeg", "lijst",
    "detail", "formulier", "volgende", "balk", "functie", "opslaan", "melding", "acties",
    "categorie", "alles", "recht", "teller", "tab", "paneel", "dialoog"
  ]

  static values = { teksten: Object, standaard: Object }

  connect() {
    this.filterWaarde = "alle"
    this.wachtendeLink = null
    this.negeerEenmaal = false

    this.frameGeladen = this.frameGeladen.bind(this)
    this.voorVerlaten = this.voorVerlaten.bind(this)
    this.voorTurboBezoek = this.voorTurboBezoek.bind(this)
    this.element.addEventListener("turbo:frame-load", this.frameGeladen)
    window.addEventListener("beforeunload", this.voorVerlaten)
    document.addEventListener("turbo:before-visit", this.voorTurboBezoek)

    this.bijwerkenNaLaden()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.frameGeladen)
    window.removeEventListener("beforeunload", this.voorVerlaten)
    document.removeEventListener("turbo:before-visit", this.voorTurboBezoek)
  }

  // ---- medewerkerslijst -------------------------------------------------

  filter() {
    const zoek = (this.hasZoekveldTarget ? this.zoekveldTarget.value : "").trim().toLowerCase()
    let zichtbaar = 0
    this.medewerkerTargets.forEach((item) => {
      const tekstPast = zoek === "" || (item.dataset.zoek || "").includes(zoek)
      const statusPast = this.filterWaarde === "alle" || item.dataset.status === this.filterWaarde
      const tonen = tekstPast && statusPast
      item.hidden = !tonen
      item.style.display = tonen ? "" : "none"
      if (tonen) zichtbaar += 1
    })
    if (this.hasLeegTarget) this.leegTarget.hidden = zichtbaar > 0
  }

  kiesFilter(event) {
    this.filterWaarde = event.currentTarget.dataset.filter
    this.filterknopTargets.forEach((knop) => {
      knop.setAttribute("aria-pressed", knop === event.currentTarget ? "true" : "false")
    })
    this.filter()
  }

  // Klik op een medewerker. Met openstaande wijzigingen eerst de dialoog.
  kies(event) {
    const link = event.currentTarget
    if (this.negeerEenmaal) {
      this.negeerEenmaal = false
      return
    }
    if (this.huidigeId() === link.dataset.medewerkerId) {
      event.preventDefault()
      return
    }
    if (this.isGewijzigd()) {
      event.preventDefault()
      this.wachtendeLink = link
      this.openDialoog()
      return
    }
    this.markeer(link.dataset.medewerkerId)
  }

  markeer(id) {
    this.medewerkerTargets.forEach((item) => {
      item.setAttribute("aria-current", item.dataset.medewerkerId === String(id) ? "true" : "false")
    })
  }

  // ---- dialoog niet-opgeslagen wijzigingen ------------------------------

  openDialoog() {
    const dialoog = this.dialoogTarget
    if (typeof dialoog.showModal === "function") {
      dialoog.showModal()
    } else {
      // Zonder <dialog>-ondersteuning: kiezen tussen opslaan en negeren
      if (window.confirm(this.teksten.verlaten)) this.negeren()
    }
  }

  sluitDialoog() {
    if (this.hasDialoogTarget && this.dialoogTarget.open) this.dialoogTarget.close()
  }

  opslaanEnDoor() {
    this.sluitDialoog()
    if (!this.wachtendeLink || !this.hasFormulierTarget) return
    if (this.hasVolgendeTarget) this.volgendeTarget.value = this.wachtendeLink.dataset.medewerkerId
    this.markeer(this.wachtendeLink.dataset.medewerkerId)
    this.wachtendeLink = null
    this.formulierTarget.requestSubmit()
  }

  negeren() {
    this.sluitDialoog()
    const link = this.wachtendeLink
    this.wachtendeLink = null
    if (!link) return
    this.momentopname = this.huidigeStand()
    this.werkIndicatorBij()
    this.negeerEenmaal = true
    link.click()
  }

  annuleren() {
    this.wachtendeLink = null
    this.sluitDialoog()
  }

  voorVerlaten(event) {
    if (!this.isGewijzigd()) return
    event.preventDefault()
    event.returnValue = this.teksten.verlaten
  }

  voorTurboBezoek(event) {
    if (!this.isGewijzigd()) return
    if (!window.confirm(this.teksten.verlaten)) event.preventDefault()
  }

  // ---- categorieën en vinkjes ---------------------------------------------

  klap(event) {
    const categorie = event.currentTarget.closest("[data-rechten-target='categorie']")
    if (!categorie) return
    const open = categorie.dataset.open !== "true"
    categorie.dataset.open = open ? "true" : "false"
    const body = categorie.querySelector(".rr-cat-body")
    if (body) body.hidden = !open
    event.currentTarget.setAttribute("aria-expanded", open ? "true" : "false")
  }

  alles(event) {
    const vink = event.currentTarget
    const categorie = vink.closest("[data-rechten-target='categorie']")
    const rechten = this.rechtenIn(categorie).filter((recht) => !recht.disabled)
    const aan = rechten.filter((recht) => recht.checked)

    if (!vink.checked && aan.length > 1) {
      const vraag = this.teksten.alles_uit
        .replace("%{aantal}", aan.length)
        .replace("%{categorie}", categorie.dataset.naam || "")
      if (!window.confirm(vraag)) {
        this.werkCategorieBij(categorie)
        return
      }
    }
    rechten.forEach((recht) => { recht.checked = vink.checked })
    this.werkCategorieBij(categorie)
    this.gewijzigd()
  }

  recht(event) {
    this.werkCategorieBij(event.currentTarget.closest("[data-rechten-target='categorie']"))
  }

  rechtenIn(categorie) {
    return Array.from(categorie.querySelectorAll("[data-rechten-target='recht']"))
  }

  werkCategorieBij(categorie) {
    if (!categorie) return
    const rechten = this.rechtenIn(categorie)
    if (rechten.length === 0) return
    const aan = rechten.filter((recht) => recht.checked).length
    const vink = categorie.querySelector("[data-rechten-target='alles']")
    if (vink) {
      vink.checked = aan === rechten.length
      vink.indeterminate = aan > 0 && aan < rechten.length
    }
    const teller = categorie.querySelector("[data-rechten-target='teller']")
    if (teller) {
      teller.textContent = this.teksten.teller.replace("%{aan}", aan).replace("%{totaal}", rechten.length)
      teller.classList.toggle("is-vol", aan === rechten.length)
    }
  }

  // ---- acties ---------------------------------------------------------------

  // Bugfix 13 (#1348): een startpunt vanuit de functietitel, geen automatisme
  functieOvernemen() {
    this.sluitActies()
    const rol = this.hasFunctieTarget ? this.functieTarget.value : ""
    const set = this.standaardValue[rol]
    if (!set) {
      window.alert(this.teksten.kies_eerst_functie)
      return
    }
    this.rechtTargets.forEach((recht) => {
      if (!recht.disabled) recht.checked = set.includes(recht.value)
    })
    this.categorieTargets.forEach((categorie) => this.werkCategorieBij(categorie))
    this.gewijzigd()
  }

  ongedaanMaken() {
    this.sluitActies()
    if (!this.hasFormulierTarget || !this.momentopname) return
    const bewaard = new Set(this.momentopname.split("&"))
    this.formulierTarget.querySelectorAll("input[type=checkbox][name]").forEach((vink) => {
      if (vink.disabled) return
      vink.checked = bewaard.has(`${encodeURIComponent(vink.name)}=${encodeURIComponent(vink.value)}`)
    })
    this.formulierTarget.querySelectorAll("select[name]").forEach((keuze) => {
      const regel = Array.from(bewaard).find((deel) => deel.startsWith(`${encodeURIComponent(keuze.name)}=`))
      if (regel) keuze.value = decodeURIComponent(regel.split("=")[1] || "")
    })
    this.categorieTargets.forEach((categorie) => this.werkCategorieBij(categorie))
    this.gewijzigd()
  }

  sluitActies() {
    if (this.hasActiesTarget) this.actiesTarget.open = false
  }

  kiesTab(event) {
    const gekozen = event.currentTarget.dataset.paneel
    this.tabTargets.forEach((tab) => tab.setAttribute("aria-selected", tab.dataset.paneel === gekozen ? "true" : "false"))
    this.paneelTargets.forEach((paneel) => { paneel.hidden = paneel.dataset.paneel !== gekozen })
  }

  // ---- stand van het formulier ---------------------------------------------

  huidigeStand() {
    if (!this.hasFormulierTarget) return ""
    const data = new FormData(this.formulierTarget)
    data.delete("volgende")
    data.delete("authenticity_token")
    data.delete("_method")
    return Array.from(data.entries())
      .map(([naam, waarde]) => `${encodeURIComponent(naam)}=${encodeURIComponent(waarde)}`)
      .sort()
      .join("&")
  }

  isGewijzigd() {
    return this.hasFormulierTarget && this.momentopname !== undefined && this.huidigeStand() !== this.momentopname
  }

  gewijzigd() {
    this.werkIndicatorBij()
  }

  werkIndicatorBij() {
    if (this.hasBalkTarget) this.balkTarget.dataset.gewijzigd = this.isGewijzigd() ? "true" : "false"
  }

  bezig() {
    if (this.hasOpslaanTarget) {
      this.opslaanTarget.disabled = true
      this.opslaanTarget.textContent = this.teksten.bezig
    }
  }

  // ---- na het laden van het frame ------------------------------------------

  // Stimulus meldt nieuwe categorieën zodra Turbo het frame heeft gevuld; de
  // indeterminate-stand bestaat alleen in JavaScript, dus die zetten we hier.
  categorieTargetConnected(categorie) {
    this.werkCategorieBij(categorie)
  }

  detailTargetConnected() {
    window.requestAnimationFrame(() => this.bijwerkenNaLaden())
  }

  frameGeladen(event) {
    if (event.target.id !== "rechten_detail") return
    this.bijwerkenNaLaden()
  }

  bijwerkenNaLaden() {
    this.categorieTargets.forEach((categorie) => this.werkCategorieBij(categorie))
    this.momentopname = this.huidigeStand()
    this.werkIndicatorBij()

    if (this.hasDetailTarget) {
      const id = this.detailTarget.dataset.medewerkerId
      this.markeer(id)
      // Een gewijzigde functietitel meteen ook in de lijst tonen
      const item = this.medewerkerTargets.find((el) => el.dataset.medewerkerId === id)
      const sub = item && item.querySelector("[data-functietitel]")
      if (sub && this.detailTarget.dataset.functietitel) sub.textContent = this.detailTarget.dataset.functietitel
      if (item && this.hasLijstTarget) this.houdInBeeld(item)
    }

    if (this.hasMeldingTarget) {
      const melding = this.meldingTarget
      // Ook als toast van de app, want na opslaan staat de melding bovenaan
      // het frame en kan de beheerder verder naar beneden gescrold zijn.
      if (typeof window.showSuccess === "function") window.showSuccess(melding.textContent.trim())
      window.setTimeout(() => { melding.remove() }, 6000)
    }
  }

  houdInBeeld(item) {
    const lijst = this.lijstTarget
    const boven = item.offsetTop - lijst.offsetTop
    if (boven < lijst.scrollTop || boven + item.offsetHeight > lijst.scrollTop + lijst.clientHeight) {
      lijst.scrollTop = boven - lijst.clientHeight / 2
    }
  }

  huidigeId() {
    return this.hasDetailTarget ? this.detailTarget.dataset.medewerkerId : null
  }

  get teksten() {
    return this.tekstenValue || {}
  }
}
